import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserAuthService } from './auth/userAuth.service';
import { Router } from '@angular/router';
import { HospitalStateService } from './services/hospitalstate.service';
import { IpdFeatureService } from './services/ipd-feature.service';
import { inject } from '@angular/core';
 
@Injectable({ providedIn: 'root' })
export class LoginService {
  private _authenticated: boolean = false;
  private _isLoggingOut: boolean = false;
  private _userResult: any = null;
 
  get isLoggingOut(): boolean {
    return this._isLoggingOut;
  }
  private _httpclient = inject(HttpClient);
  private _userService = inject(UserAuthService);
  private _router = inject(Router);
  private _hospital = inject(HospitalStateService);
  private _ipdFeature = inject(IpdFeatureService);
 
  constructor() { }
 
  // Access token is now handled by HttpOnly cookies.
  // JS no longer has access to it.
  get accessToken(): string {
    return "";
  }
 
  private getDecodedToken(): any | null {
    return this._userResult;
  }

  signIn(credentials: { username: string; password: string }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      this._router.navigate(['/dashboard']);
      // return throwError('User is already logged in.');
    }

    return this._httpclient.post(`${environment.baseURl}/Auth/login`, credentials, { withCredentials: true }).pipe(
      switchMap((response: any) => {
        this._userResult = response.result;
        this._authenticated = true;

        this._userService.user = response.result.userName;

        const hospitalId = this.getHospitalId() as number;
        return this._userService.getHospital(hospitalId).pipe(
          tap(res => {
            this._hospital.setHospital(res.result);
          }),
          map(() => response)
        );
      })
    );
  }

  signOut(): Observable<any> {
    // Prevent multiple logout calls
    if (this._isLoggingOut) {
      return of(true);
    }

    this._isLoggingOut = true;

    return this._httpclient.post(`${environment.baseURl}/Auth/logout`, {}, { withCredentials: true }).pipe(
      tap(() => {
        this._clearSessionAndNavigate();
      }),
      catchError((error) => {
        this._clearSessionAndNavigate();

        return of(true);
      }),
      tap(() => {
        this._isLoggingOut = false;
      })
    );
  }

  private _clearSessionAndNavigate(): void {
    this._hospital.clearHospital();
    this._userResult = null;
    this._authenticated = false;
    this._router.navigate(['authentication/login']);
  }

  isIPDTabShown(): boolean {
    return this._ipdFeature.isEnabled;
  }

  initializeAuth() {
    // debugger;
    return firstValueFrom(
      this._httpclient.get(`${environment.baseURl}/Auth/me`, { withCredentials: true }).pipe(
        tap((response: any) => {
          this._userResult = response.result;
          this._authenticated = true;
          this._userService.user = response.result.userName;
        }),
        switchMap((response: any) => {
          const hospitalId = response.result.hospitalId;
          if (hospitalId) {
            return this._userService.getHospital(hospitalId).pipe(
              tap(res => {
                this._hospital.setHospital(res.result);
              }),
              catchError(() => of(null))
            );
          }
          return of(null);
        }),
        catchError(() => {
          this._authenticated = false;
          return of(null);
        })
      )
    );
  }
 
  getHospitalFromToken(): string[] | null {
    if (!this._userResult) return null;
    return this._userResult.hospitalId ? [this._userResult.hospitalId.toString()] : [];
  }

  getHospitalId(): number | null {
    return this._userResult?.hospitalId ? Number(this._userResult.hospitalId) : null;
  }

  getUserRoleFromToken(): string[] | null {
    return this._userResult?.roles;
  }

  isDoctor(): boolean {
    const roles = this.getUserRoleFromToken();
    return roles?.includes("Doctor") ?? false;
  }

  getUserId(): number | null {
    return this._userResult?.id ? Number(this._userResult.id) : null;
  }
}